import {
    IAgentRuntime,
    Memory,
    Provider,
    State,
    elizaLogger,
} from "@elizaos/core";
import { ethers } from "ethers";
import { CotiProviderConfig } from "../types/index";

export class CotiProvider {
    private provider: ethers.providers.JsonRpcProvider;
    private config: CotiProviderConfig;

    constructor(config: CotiProviderConfig) {
        this.config = config;
        this.provider = new ethers.providers.JsonRpcProvider(config.rpcUrl);
        elizaLogger.info(`COTI Provider initialized with RPC: ${config.rpcUrl}`);
    }

    async getBlockNumber(): Promise<number> {
        return await this.provider.getBlockNumber();
    }

    async getGasPrice(): Promise<ethers.BigNumber> {
        return await this.provider.getGasPrice();
    }

    async getBalance(address: string): Promise<ethers.BigNumber> {
        return await this.provider.getBalance(address);
    }

    async getTransactionCount(address: string): Promise<number> {
        return await this.provider.getTransactionCount(address);
    }

    async getTransaction(txHash: string): Promise<ethers.providers.TransactionResponse | null> {
        return await this.provider.getTransaction(txHash);
    }

    async getTransactionReceipt(txHash: string): Promise<ethers.providers.TransactionReceipt | null> {
        return await this.provider.getTransactionReceipt(txHash);
    }

    async estimateGas(transaction: ethers.providers.TransactionRequest): Promise<ethers.BigNumber> {
        return await this.provider.estimateGas(transaction);
    }

    getProvider(): ethers.providers.JsonRpcProvider {
        return this.provider;
    }

    getNetworkInfo() {
        return {
            rpcUrl: this.config.rpcUrl,
            networkId: this.config.networkId,
        };
    }
}

// Global provider instance
let cotiProviderInstance: CotiProvider | null = null;

export function initCotiProvider(): CotiProvider {
    if (!cotiProviderInstance) {
        const config: CotiProviderConfig = {
            rpcUrl: process.env.COTI_RPC_URL || "https://testnet.coti.io/rpc",
            networkId: parseInt(process.env.COTI_NETWORK_ID || "7701"),
            timeout: parseInt(process.env.COTI_TIMEOUT || "30000"),
        };
        
        cotiProviderInstance = new CotiProvider(config);
    }
    
    return cotiProviderInstance;
}

export const cotiProvider: Provider = {
    name: "COTI_PROVIDER",
    description: "Provides COTI blockchain network information and utilities",

    get: async (
        runtime: IAgentRuntime,
        message: Memory,
        state: State | undefined
    ) => {
        try {
            const provider = initCotiProvider();
            const blockNumber = await provider.getBlockNumber();
            const gasPrice = await provider.getGasPrice();
            const networkInfo = provider.getNetworkInfo();

            return {
                text: `COTI Network - Block: ${blockNumber}, Gas Price: ${ethers.utils.formatUnits(gasPrice, "gwei")} gwei, Network ID: ${networkInfo.networkId}`,
                values: {
                    blockNumber,
                    gasPrice: ethers.utils.formatUnits(gasPrice, "gwei"),
                    networkId: networkInfo.networkId,
                    rpcUrl: networkInfo.rpcUrl,
                },
                data: {
                    blockNumber,
                    gasPrice: gasPrice.toString(),
                    gasPriceGwei: ethers.utils.formatUnits(gasPrice, "gwei"),
                    networkId: networkInfo.networkId,
                    rpcUrl: networkInfo.rpcUrl,
                },
            };
        } catch (error) {
            elizaLogger.error("Error in COTI provider:", error);
            return {
                text: `Error accessing COTI network: ${error instanceof Error ? error.message : String(error)}`,
                values: {},
                data: { error: error instanceof Error ? error.message : String(error) },
            };
        }
    },
};
